import { useState, type FormEvent } from "react";
import { jobsNamed, parseJobReference, type Job, type JobReference } from "@domain/CRM/job";
import { API } from "@gui/infrastructure/pass-through/api";
import { Oops } from "@gui/infrastructure/pass-through/oops";

export interface JobLookupProps {
	value: string;
	onValueChange: (value: string) => void;
	/** Takes the job the lookup found. May be async; the box stays busy until it settles. */
	onFound: (job: Job) => void | Promise<void>;
}

type Lookup = Exclude<JobReference, { kind: "invalid" }>;

/** Reads the job the reference names. A name goes through the jobs filter and is then matched
 *  whole on this side too — the offline fake ignores filters — and a name Airtable holds twice
 *  (an archived skip and its continuation, say) is a question for the operator, not a coin toss. */
async function findJob(ref: Lookup): Promise<Job> {
	if (ref.kind === "id") return API.Data.Jobs.get.byId(ref.id, false);
	const matches = jobsNamed(await API.Data.Jobs.get.byFilter({ fields: { name: ref.name } }), ref.name);
	if (matches.length === 1) return matches[0];
	if (matches.length === 0) throw new Error(`No job is named "${ref.name}".`);
	const ids = matches.map((job) => job.id).join(", ");
	throw new Error(`${matches.length} jobs are named that (${ids}) — paste the record id of the one you mean.`);
}

const describeFailure = (err: unknown, ref: Lookup): string => {
	const message = err instanceof Error ? err.message : String(err);
	if (ref.kind === "id" && Oops.isNotFound(err)) return `Job not found: ${ref.id}`;
	return message || "Failed to load the job.";
};

export const useJobLookupViewModel = ({ value, onValueChange, onFound }: JobLookupProps) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const edit = (next: string) => {
		onValueChange(next);
		setError(null);
	};
	const clear = () => edit("");

	const lookUp = async () => {
		const ref = parseJobReference(value);
		if (ref.kind === "invalid") {
			// A blank box gets no lecture — the button is disabled for it anyway.
			if (value.trim()) setError(ref.reason);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			await onFound(await findJob(ref));
		} catch (err) {
			setError(describeFailure(err, ref));
		} finally {
			setLoading(false);
		}
	};

	const submit = (event: FormEvent) => {
		event.preventDefault();
		// The submit button stays pressable while busy (BusyButton), so a second press lands here.
		if (loading) return;
		void lookUp();
	};

	return { value, edit, clear, submit, lookUp, loading, error, canSubmit: !loading && value.trim() !== "" };
};
